import { useSyncExternalStore } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSessionKey } from "../auth/useSessionKey";
import {
  composedUnder,
  participationVersion,
  stamped,
  subscribeToParticipation,
} from "./answerSession";

/**
 * WHAT THE PARTICIPANT HAS ANSWERED SO FAR, carried from step to step.
 *
 * ⚠️ IT IS NOT THE ORGANISER'S READING SCOPE, and that separation is the point.
 * The typology step used to call `setTypo`, which moves the "as" selector in the
 * organiser bar — so a click meant as a participant silently changed WHICH
 * TYPOLOGY THE PANEL WRITES TO. An organiser previewing "every participant" would
 * have been editing an exhibitor's overrides without a word. The participant's
 * answers live here; the reading scope stays the organiser's alone.
 *
 * ⚠️ AND IT COMES FROM `history.state`, so nothing here is trusted. It survives a
 * back button, it can be crafted by hand, and it is JSON somebody else's tab may
 * have written. Every field is checked; what does not typecheck is dropped rather
 * than carried into the confirmation, where it would decide what the screen says.
 *
 * Answers travel in the navigation state rather than in the address: they are
 * nobody's business in a shared link, and a confirmation reached by a pasted URL
 * must not claim an answer that was never given.
 */

export type Answers = {
  /** `onsite` · `remote` · `represented` · `decline` */
  participation?: string;
  /** Why they are not coming, when the organiser asks. */
  reason?: string;
  /** Who comes instead, when they are being represented. */
  stand?: { first: string; last: string; email: string };
  /** The profile they picked — the event's own typology id. */
  typoId?: number;
  /**
   * Les jours cochés — des `EventDays.id`.
   *
   * ⚠️ RIEN NE GARANTIT QU'ILS SOIENT DE CET ÉVÉNEMENT : comme tout ce qui vit ici,
   * ils viennent de `history.state`. Ce qui n'est pas un nombre est écarté ci-dessous ;
   * ce qui n'est pas un jour de l'événement l'est à l'écriture — voir `joursRetenus`.
   */
  days?: number[];
  /**
   * LES ARTICLES RETENUS, PAR JOUR — `{ "0": [12], "1643": [8] }`.
   *
   * ⚠️ MÊME DÉFIANCE QUE LE RESTE, et ici elle a un coût précis : la route qui chiffre
   * une sélection empile les articles qu'elle ne retrouve pas sans les vérifier, puis
   * lit une propriété sur `undefined`. Un identifiant fantaisiste glissé dans
   * `history.state` rendrait donc un 500 au lieu d'un total — d'où le filtrage ici, et
   * le second contre le catalogue à l'envoi (`idsAplat`).
   */
  articles?: Record<string, number[]>;
  /**
   * LE RANG LE PLUS LOIN ATTEINT DANS LE FIL — un entier, et rien d'autre.
   *
   * Il sert au FIL D'ARIANE, à lui seul : revenir sur l'étape 3 ne doit pas effacer le
   * fait qu'on est allé jusqu'à la 4, sans quoi corriger une réponse coûte un
   * « Continuer » de plus par écran déjà traversé.
   *
   * ⚠️ ET AUCUNE GARDE NE LE LIT, c'est la condition pour qu'il existe. La liste d'étapes
   * franchies qui vivait ici avant lui servait de PREUVE d'avoir répondu : elle survit
   * aux rechargements, donc elle faisait passer pour inscrit qui n'avait fait que
   * traverser, et un F5 sur la confirmation créait des participations. Ce qui décide
   * d'ouvrir le parcours ou de renvoyer au début reste `responded`, côté serveur — voir
   * `funnel/Steps.tsx` et `Shell.tsx`. Celui-ci ne décide de rien : au pire, un rang
   * fantaisiste rend une étape cliquable une étape trop tôt.
   */
  loin?: number;
};

const PARTICIPATION = ["onsite", "remote", "represented", "decline"];

function text(raw: unknown): string | undefined {
  return typeof raw === "string" ? raw : undefined;
}

export function readAnswers(state: unknown): Answers {
  if (!state || typeof state !== "object") return {};
  const from = state as Record<string, unknown>;

  const participation = text(from.participation);
  const stand =
    from.stand && typeof from.stand === "object"
      ? (from.stand as Record<string, unknown>)
      : null;

  return {
    // An unknown answer is dropped: the confirmation picks its wording from this,
    // and a value nobody can produce would land it on the wrong branch.
    ...(participation && PARTICIPATION.includes(participation)
      ? { participation }
      : {}),
    ...(text(from.reason) !== undefined ? { reason: text(from.reason) } : {}),
    ...(stand
      ? {
          stand: {
            first: text(stand.first) ?? "",
            last: text(stand.last) ?? "",
            email: text(stand.email) ?? "",
          },
        }
      : {}),
    ...(typeof from.typoId === "number" && Number.isFinite(from.typoId)
      ? { typoId: from.typoId }
      : {}),
    /* Un tableau de nombres, ou rien. ⚠️ UNE LISTE VIDE SE GARDE : c'est « j'ai tout
       décoché », et l'écarter la confondait avec « je n'ai pas répondu » — le fil
       d'Ariane ouvrait alors la confirmation sur les jours du serveur, ceux qu'on venait
       de décocher (bug #257). Elle n'inscrit pas pour autant à zéro jour :
       `selectedDaysOf` la lit comme une absence de choix à l'écriture, et le fil ferme
       ce qui suit l'étape tant qu'elle est vide (`datesAnswered`). Ce qui n'est pas un
       nombre, en revanche, reste écarté — une liste de seules fantaisies ne dit rien. */
    ...(Array.isArray(from.days) &&
    (from.days.length === 0 ||
      from.days.some((d) => typeof d === "number" && Number.isFinite(d)))
      ? {
          days: from.days.filter(
            (d): d is number => typeof d === "number" && Number.isFinite(d),
          ),
        }
      : {}),
    /* Un objet de listes d'identifiants, ou rien. Une sélection VIDE se garde ici,
       contrairement aux jours : « je n'ai rien pris » est une réponse en billetterie —
       la distinguer de l'absence est ce qui permet à quelqu'un de tout décocher sans
       voir revenir sa commande précédente. */
    ...(readArticles(from.articles) ?? {}),
    /* Un entier de rang, ou rien. Ce qui vient de `history.state` n'est pas de confiance,
       et un « rang » négatif ou décimal n'en est pas un. */
    ...(typeof from.loin === "number" &&
    Number.isInteger(from.loin) &&
    from.loin >= 0
      ? { loin: from.loin }
      : {}),
  };
}

/**
 * WHAT IS NOT AN ANSWER BUT BOOKKEEPING.
 *
 * ⚠️ ONE FIELD, AND IT WAS EXPENSIVE. `loin` is the furthest rank reached, written by
 * the shell on EVERY arrival at a step — the confirmation included. Counted as an
 * answer, it made "this person has just answered" true for anybody who merely OPENS
 * their recap, and the screen then wrote a registration nobody had given. Measured:
 * three PUTs on a plain visit, one of them pushing `registeredAt` to now. That is
 * exactly what `loin`'s own documentation forbids — "no guard reads it, that is the
 * condition for it to exist".
 */
const BOOKKEEPING: ReadonlyArray<keyof Answers> = ["loin"];

/**
 * A-T-ON RÉPONDU QUELQUE CHOSE DANS CE PARCOURS ?
 *
 * ⚠️ « DANS CE PARCOURS », ET NON « EN GÉNÉRAL ». Cet état ne porte que ce qui vient
 * d'être saisi et n'est pas encore écrit ; savoir si la personne a répondu à l'événement
 * est une question pour le serveur, et sa réponse s'appelle `responded`. Confondre les
 * deux est ce qui laissait une simple traversée passer pour une inscription.
 */
export function hasAnswered(answers: Answers): boolean {
  return answeredFields(answers).length > 0;
}

/** Les champs réellement répondus dans ce parcours, la tenue de compte mise de côté. */
export function answeredFields(answers: Answers): Array<keyof Answers> {
  return (Object.keys(answers) as Array<keyof Answers>).filter(
    (field) => answers[field] !== undefined && !BOOKKEEPING.includes(field),
  );
}

/** `{ articles }` quand il y en a, rien quand la valeur n'en est pas une. */
function readArticles(
  raw: unknown,
): { articles: Record<string, number[]> } | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;

  const articles: Record<string, number[]> = {};
  for (const [day, list] of Object.entries(raw as Record<string, unknown>)) {
    if (!Array.isArray(list)) continue;
    articles[day] = list.filter(
      (id): id is number => typeof id === "number" && Number.isFinite(id),
    );
  }
  return { articles };
}

/**
 * The answers of the current step, and a way to record one INTO THIS HISTORY
 * ENTRY.
 *
 * ⚠️ `replace`, not a push. A choice written only into the state of the NEXT entry
 * is lost the moment somebody presses Back — measured: the profile came back
 * undefined, the card unselected, and the step looked as if it had never been
 * answered. Recording it here means the browser restores it with the entry.
 *
 * Only CLICKS are recorded this way. Doing it per keystroke would replace the
 * history entry on every letter typed; text answers are instead carried forward
 * when the participant moves on, and re-seeded from that when they come back
 * through the step's own "Retour".
 */
export function useAnswers(): [Answers, (patch: Answers) => void] {
  const location = useLocation();
  const navigate = useNavigate();
  const session = useSessionKey();
  /* Relu quand le serveur rend une autre participation : des réponses composées sous
     celle qui n'existe plus disparaissent (voir `answerSession.ts`). */
  useSyncExternalStore(subscribeToParticipation, participationVersion, participationVersion);
  const answers = answersOf(location.state, session);

  const record = (patch: Answers) =>
    navigate(location.pathname + location.search, {
      replace: true,
      state: stamped({ ...answers, ...patch }, session),
    });

  return [answers, record];
}

/** Les réponses portées par cet état, si elles ont été données sous cette session ; sinon aucune (bug 8 : voir `answerSession.ts`). */
export function answersOf(state: unknown, session: string): Answers {
  return composedUnder(state, session) ? readAnswers(state) : {};
}
